import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import type HotelSettingsUsersController from 'lumen/controllers/hotel/settings/users';
import type ManagedUser from 'lumen/models/managed-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface SubscriptionUserMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface SubscriptionUserData {
  meta: SubscriptionUserMeta;
  users: SubscriptionUser[];
}

export interface ManagedUserData {
  meta: SubscriptionUserMeta;
  users: ManagedUser[];
}

export interface RouteParams {
  page?: number;
  product?: string;
  role?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  subscription?: string;
}

export interface UsersModel {
  loadManagedUsersTask: typeof HotelSettingsUsersRoute.prototype.loadManagedUsersTask;
  loadSubscriptionOptionTask: typeof HotelSettingsUsersRoute.prototype.loadSubscriptionOptionTask;
  loadUsersTask: typeof HotelSettingsUsersRoute.prototype.loadUsersTask;
}

export default class HotelSettingsUsersRoute extends Route {
  queryParams = {
    search: { refreshModel: true },
    role: { refreshModel: true },
    product: { refreshModel: true },
    sortBy: { refreshModel: true },
    sortDirection: { refreshModel: true },
    subscription: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadManagedUsersTask = task(
    { restartable: true },
    async (params: RouteParams) => {
      try {
        const response = await this.store.query<ManagedUser>('managed-user', {
          namespace: APIGATEWAY,
          typeKey: 'users/me/managed-users',
          // The managed-users endpoint only sorts on `name`, so a `role` sort
          // carried over from the subscription view falls back to `name`.
          sort: `${params.sortDirection === 'desc' ? '-' : ''}name`,
          page: params.page ?? 1,
          ...(params.search ? { 'filter[q]': params.search } : {}),
        });

        return {
          users: [...response],
          meta: response.meta as unknown as SubscriptionUserMeta,
        } satisfies ManagedUserData;
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );
  /**
   * A subscription is only known by its id when landing on the route with a
   * subscription query param, so resolve the option the select needs from the
   * hotel that subscription belongs to.
   */
  loadSubscriptionOptionTask = task(
    { drop: true },
    async (subscriptionId: string): Promise<SubscriptionOption | undefined> => {
      try {
        const hotel =
          this.store.peekRecord<Hotel>('hotel', subscriptionId) ??
          (await this.store.findRecord<Hotel>('hotel', subscriptionId));

        return { id: subscriptionId, label: hotel.hotelinfo.name };
      } catch (error) {
        this.errorTracking.captureException(error);
        return undefined;
      }
    },
  );
  loadUsersTask = task({ restartable: true }, async (params: RouteParams) => {
    try {
      const response = await this.store.query<SubscriptionUser>(
        'subscription-user',
        {
          namespace: APIGATEWAY,
          typeKey: `subscriptions/${params.subscription}/users`,
          sort: `${params.sortDirection === 'desc' ? '-' : ''}${params.sortBy ?? 'name'}`,
          page: params.page ?? 1,
          ...(params.search ? { 'filter[q]': params.search } : {}),
          ...(params.role ? { 'filter[role]': params.role } : {}),
          ...(params.product ? { 'filter[product]': params.product } : {}),
        },
      );

      return {
        users: [...response],
        meta: response.meta as unknown as SubscriptionUserMeta,
      } satisfies SubscriptionUserData;
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }
  });

  beforeModel(transition: Transition): void {
    if (!this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.USER_ACCESS)) {
      this.transition.redirectToTab(transition, 'hotel.settings.index');
    }
  }

  model(): UsersModel {
    return {
      loadManagedUsersTask: this.loadManagedUsersTask,
      loadSubscriptionOptionTask: this.loadSubscriptionOptionTask,
      loadUsersTask: this.loadUsersTask,
    };
  }

  setupController(
    controller: HotelSettingsUsersController,
    model: UsersModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    void controller.resetAndLoad();
  }
}
